<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="container-custom max-w-2xl">
      <h1 class="text-3xl font-bold text-gray-900 text-center mb-8">
        {{ $t('order.track.title') }}
      </h1>
      
      <!-- Search Form -->
      <div class="card p-6 mb-8">
        <div class="flex gap-4">
          <input
            v-model="searchNumber"
            type="text"
            :placeholder="$t('order.track.enterNumber')"
            class="flex-1 form-input"
          >
          <button
            @click="trackOrder"
            class="px-6 py-2 rounded-lg text-white font-medium"
            :style="{ backgroundColor: companyStore.primaryColor }"
          >
            {{ $t('order.track.track') }}
          </button>
        </div>
      </div>
      
      <!-- Saved Order Hint -->
      <div v-if="savedOrderNumber && !order" class="card p-6 mb-8 bg-orange-50 border-orange-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-orange-700 mb-1">{{ $t('order.track.savedOrder') }}</p>
            <p class="text-xl font-bold text-orange-900">#{{ savedOrderNumber }}</p>
          </div>
          <div class="flex gap-2">
            <button
              @click="copySavedNumber"
              class="px-4 py-2 rounded-lg border border-orange-300 text-orange-700 font-medium hover:bg-orange-100 text-sm"
            >
              {{ copied ? $t('order.track.copied') : $t('order.track.copyNumber') }}
            </button>
            <button
              @click="trackSavedOrder"
              class="px-4 py-2 rounded-lg text-white font-medium text-sm"
              :style="{ backgroundColor: companyStore.primaryColor }"
            >
              {{ $t('order.track.track') }}
            </button>
          </div>
        </div>
      </div>
      
      <!-- Order Details -->
      <div v-if="order" class="card p-6">
        <!-- Copyable Order Number Banner -->
        <div v-if="order.status !== 'delivered' && order.status !== 'cancelled'" class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-orange-700 mb-1">{{ $t('order.track.yourOrderNumber') }}</p>
              <p class="text-2xl font-bold text-orange-900">#{{ order.order_number }}</p>
            </div>
            <button
              @click="copyOrderNumber"
              class="px-4 py-2 rounded-lg border border-orange-300 text-orange-700 font-medium hover:bg-orange-100 text-sm"
            >
              {{ copied ? $t('order.track.copied') : $t('order.track.copyNumber') }}
            </button>
          </div>
        </div>
        
        <div class="flex items-center justify-between mb-6">
          <div>
            <p class="text-sm text-gray-500">{{ $t('order.number', { number: order.order_number }) }}</p>
            <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(order.total) }}</p>
          </div>
          <span
            class="badge"
            :class="getStatusClass(order.status)"
          >
            {{ $t(`order.status.${order.status}`) }}
          </span>
        </div>
        
        <!-- Status Timeline -->
        <div class="relative">
          <div class="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
          
          <div class="space-y-6">
            <div
              v-for="(step, index) in statusSteps"
              :key="step.status"
              class="relative flex items-start gap-4"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center z-10"
                :class="{
                  'text-white': isStepCompleted(step.status),
                  'bg-gray-200 text-gray-400': !isStepCompleted(step.status)
                }"
                :style="isStepCompleted(step.status) ? { backgroundColor: companyStore.primaryColor } : {}"
              >
                <CheckIcon v-if="isStepCompleted(step.status)" class="h-5 w-5" />
                <span v-else class="text-sm">{{ index + 1 }}</span>
              </div>
              <div>
                <p class="font-medium" :class="{ 'text-gray-900': isStepCompleted(step.status), 'text-gray-500': !isStepCompleted(step.status) }">
                  {{ step.label }}
                </p>
                <p v-if="getStepTime(step.status)" class="text-sm text-gray-500">
                  {{ formatDateTime(getStepTime(step.status)!) }}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Items -->
        <div class="mt-8 pt-6 border-t">
          <h3 class="font-semibold text-gray-900 mb-4">Itens do pedido</h3>
          <div v-if="!order.items || order.items.length === 0" class="text-gray-500 text-sm">
            Nenhum item encontrado
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="item in order.items"
              :key="item.id"
              class="flex justify-between text-sm"
            >
              <span>{{ item.quantity }}x {{ item.product_name }}</span>
              <span>{{ formatCurrency(item.total) }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Not Found -->
      <div v-else-if="searched" class="text-center py-12">
        <ExclamationCircleIcon class="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-medium text-gray-900">
          {{ $t('order.track.notFound') }}
        </h3>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { CheckIcon, ExclamationCircleIcon } from '@heroicons/vue/24/outline'
import { useCompanyStore } from '@/stores'
import { orderApi } from '@/utils/api'
import { formatCurrency, formatDateTime } from '@/utils/formatters'
import type { Order, OrderStatus } from '@/types'

const route = useRoute()
const companyStore = useCompanyStore()

const searchNumber = ref('')
const order = ref<Order | null>(null)
const searched = ref(false)
const savedOrderNumber = ref('')
const copied = ref(false)
let refreshInterval: ReturnType<typeof setInterval> | null = null

const startAutoRefresh = () => {
  stopAutoRefresh()
  refreshInterval = setInterval(() => {
    if (searchNumber.value) {
      trackOrder()
    }
  }, 300000) // 5 minutos
}

const stopAutoRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

const loadSavedOrder = () => {
  const saved = localStorage.getItem('lastOrderNumber')
  const savedCompanyId = localStorage.getItem('lastOrderCompanyId')
  const currentCompanyId = companyStore.company?.id
  if (saved && (!savedCompanyId || !currentCompanyId || savedCompanyId === String(currentCompanyId))) {
    savedOrderNumber.value = saved
  }
}

const trackSavedOrder = () => {
  if (savedOrderNumber.value) {
    searchNumber.value = savedOrderNumber.value
    trackOrder()
  }
}

const copySavedNumber = () => {
  if (savedOrderNumber.value) {
    navigator.clipboard.writeText(savedOrderNumber.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  }
}

const copyOrderNumber = () => {
  if (order.value?.order_number) {
    navigator.clipboard.writeText(order.value.order_number)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  }
}

const statusSteps = [
  { status: 'pending', label: 'Pedido recebido' },
  { status: 'confirmed', label: 'Pedido confirmado' },
  { status: 'preparing', label: 'Em preparação' },
  { status: 'ready', label: 'Pronto para entrega' },
  { status: 'out_for_delivery', label: 'Saiu para entrega' },
  { status: 'delivered', label: 'Entregue' }
]

const getStatusClass = (status: OrderStatus) => {
  const classes: Record<string, string> = {
    pending: 'status-pending',
    confirmed: 'status-confirmed',
    preparing: 'status-preparing',
    ready: 'status-ready',
    out_for_delivery: 'status-out-for-delivery',
    delivered: 'status-delivered',
    cancelled: 'status-cancelled'
  }
  return classes[status] || 'badge-gray'
}

const isStepCompleted = (stepStatus: string) => {
  const stepIndex = statusSteps.findIndex(s => s.status === stepStatus)
  const currentIndex = statusSteps.findIndex(s => s.status === order.value?.status)
  return stepIndex <= currentIndex
}

const getStepTime = (stepStatus: string): string | null => {
  if (!order.value) return null
  
  const times: Record<string, string | undefined> = {
    pending: order.value.created_at,
    confirmed: order.value.confirmed_at,
    preparing: order.value.prepared_at,
    out_for_delivery: order.value.out_for_delivery_at,
    delivered: order.value.delivered_at
  }
  
  return times[stepStatus] || null
}

const clearSavedOrder = () => {
  localStorage.removeItem('lastOrderNumber')
  localStorage.removeItem('lastOrderCompanyId')
  savedOrderNumber.value = ''
}

const trackOrder = async () => {
  if (!searchNumber.value) return
  
  try {
    const response = await orderApi.getByNumber(searchNumber.value, {
      _skipErrorToast: true
    })
    order.value = response.data
    searched.value = true
    
    // Atualiza localStorage se o pedido ainda não foi entregue/cancelado
    if (order.value) {
      if (order.value.status === 'delivered' || order.value.status === 'cancelled') {
        clearSavedOrder()
      } else {
        localStorage.setItem('lastOrderNumber', order.value.order_number)
        if (companyStore.company?.id) {
          localStorage.setItem('lastOrderCompanyId', String(companyStore.company.id))
        }
      }
    }
  } catch (error: any) {
    order.value = null
    searched.value = true
    
    // Se o pedido não foi encontrado, expirado ou entregue, limpa o último pedido salvo
    if (error.response?.status === 404) {
      if (savedOrderNumber.value === searchNumber.value) {
        clearSavedOrder()
      }
    }
  }
}

watch(() => companyStore.company?.id, () => {
  loadSavedOrder()
}, { immediate: true })

onMounted(() => {
  if (route.query.number) {
    searchNumber.value = route.query.number as string
    trackOrder()
  } else if (savedOrderNumber.value) {
    searchNumber.value = savedOrderNumber.value
    trackOrder()
  }
  
  startAutoRefresh()
})

onUnmounted(() => {
  stopAutoRefresh()
})
</script>
